import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Pago' },
  { value: 'processing', label: 'Em processamento' },
  { value: 'shipped', label: 'Enviado' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'cancelled', label: 'Cancelado' },
];

const PAYMENT_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Pago' },
  { value: 'failed', label: 'Falhou' },
  { value: 'refunded', label: 'Estornado' },
];

export function OrdersPage({
  loading,
  error,
  data,
  detail,
  selectedOrderId,
  onSelectOrder,
  onUpdateOrder,
  onCancelOrder,
  onFilter,
  updating,
  cancelling,
}) {
  const [status, setStatus] = useState('pending');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [shipping, setShipping] = useState('0');
  const [discount, setDiscount] = useState('0');
  const orders = data?.items || [];
  const summary = data?.summary || {};
  const filters = data?.filters || { status: 'all', payment_status: 'all' };
  const selectedOrder = detail || orders.find((order) => Number(order.id) === Number(selectedOrderId)) || null;

  useEffect(() => {
    if (selectedOrder) {
      setStatus(selectedOrder.status || 'pending');
      setPaymentStatus(selectedOrder.payment_status || 'pending');
      setShipping(String(selectedOrder.shipping ?? 0));
      setDiscount(String(selectedOrder.discount ?? 0));
    }
  }, [selectedOrder]);

  if (loading) return <LoadingBlock label="Carregando pedidos..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard title="Pedidos" subtitle={`${summary.total_orders || 0} pedidos • ${summary.pending_orders || 0} pendentes • R$ ${Number(summary.revenue_total || 0).toFixed(2)} recebidos`}>
        <div className="callout-box compact-box admin-callout">
          <span className="eyebrow">Acesso do administrador</span>
          <strong>Controle total da operação de pedidos</strong>
          <p>Como dono ou admin, você acompanha pedidos, Mercado Pago, taxa Perifa, líquido do vendedor, histórico e cancelamentos necessários.</p>
        </div>

        <div className="callout-grid admin-summary-grid">
          <div className="callout-box compact-box"><span className="eyebrow">Pedidos</span><strong>{summary.total_orders || 0}</strong><p>Volume total do ciclo atual.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Pendentes</span><strong>{summary.pending_orders || 0}</strong><p>Foco operacional imediato.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Pagos</span><strong>{summary.paid_orders || 0}</strong><p>Pedidos com pagamento confirmado.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Receita</span><strong>R$ {Number(summary.revenue_total || 0).toFixed(2)}</strong><p>Recebimento consolidado.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Taxa Perifa</span><strong>R$ {Number(summary.marketplace_fee_total || 0).toFixed(2)}</strong><p>Comissão sobre vendas pagas.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Líquido vendedor</span><strong>R$ {Number(summary.seller_net_total || 0).toFixed(2)}</strong><p>Valor estimado para times/lojas.</p></div>
        </div>

        <div className="toolbar-row">
          <select value={filters.status || 'all'} onChange={(e) => onFilter({ ...filters, status: e.target.value })}>
            <option value="all">Todos os status</option>
            {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <select value={filters.payment_status || 'all'} onChange={(e) => onFilter({ ...filters, payment_status: e.target.value })}>
            <option value="all">Todos os pagamentos</option>
            {PAYMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>

        <DataTable
          rows={orders}
          columns={[
            { key: 'order_number', label: 'Pedido' },
            { key: 'user_name', label: 'Cliente' },
            { key: 'seller_team_name', label: 'Time vendedor' },
            { key: 'total', label: 'Total' },
            {
              key: 'marketplace_fee',
              label: 'Taxa Perifa',
              render: (row) => `R$ ${Number(row.marketplace_fee || 0).toFixed(2)}`,
            },
            { key: 'status', label: 'Status', type: 'status' },
            { key: 'payment_status', label: 'Pagamento', type: 'status' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => (
                <button className="inline-button" onClick={() => onSelectOrder(row.id)}>
                  Abrir operação
                </button>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Operação do pedido"
        subtitle="Detalhe, itens, histórico e atualização operacional."
        actions={selectedOrder ? (
          <div className="inline-actions">
            <button className="ghost-button" type="button" onClick={() => onUpdateOrder(selectedOrder.id, {
              status,
              payment_status: paymentStatus,
              shipping: Number(shipping),
              discount: Number(discount),
            })} disabled={updating}>
              {updating ? 'Atualizando...' : 'Salvar pedido'}
            </button>
            <button className="ghost-button danger-text" type="button" onClick={() => onCancelOrder(selectedOrder.id)} disabled={cancelling || ['shipped', 'delivered', 'cancelled'].includes(selectedOrder.status)}>
              {cancelling ? 'Cancelando...' : 'Cancelar pedido'}
            </button>
          </div>
        ) : null}
      >
        {selectedOrder ? (
          <div className="detail-stack">
            <div className="callout-grid">
              <div className="callout-box compact-box"><span className="eyebrow">Pedido</span><strong>{selectedOrder.order_number}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Cliente</span><strong>{selectedOrder.user_name || selectedOrder.customer_name}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Total</span><strong>R$ {Number(selectedOrder.total || 0).toFixed(2)}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Mercado Pago</span><strong>{selectedOrder.payment_provider === 'mercado_pago' ? 'Conectado' : 'Manual'}</strong><p>{selectedOrder.payment_external_id || selectedOrder.payment_method || '-'}</p></div>
              <div className="callout-box compact-box"><span className="eyebrow">Taxa Perifa</span><strong>R$ {Number(selectedOrder.marketplace_fee || 0).toFixed(2)}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Líquido vendedor</span><strong>R$ {Number(selectedOrder.seller_net_amount || 0).toFixed(2)}</strong><p>{selectedOrder.seller_team_name || 'Sem time vinculado'}</p></div>
              <div className="callout-box compact-box"><span className="eyebrow">Cupom</span><strong>{selectedOrder.coupon_code || '-'}</strong><p>Desconto: R$ {Number(selectedOrder.discount || 0).toFixed(2)}</p></div>
            </div>

            <form
              className="admin-form"
              onSubmit={(event) => {
                event.preventDefault();
                onUpdateOrder(selectedOrder.id, {
                  status,
                  payment_status: paymentStatus,
                  shipping: Number(shipping),
                  discount: Number(discount),
                });
              }}
            >
              <div className="form-row">
                <label>
                  Status do pedido
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label>
                  Status do pagamento
                  <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                    {PAYMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label>
                  Frete
                  <input type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} />
                </label>
                <label>
                  Desconto
                  <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                </label>
              </div>
              <div className="inline-actions">
                <button className="primary-button" type="submit" disabled={updating}>
                  {updating ? 'Atualizando...' : 'Salvar pedido'}
                </button>
                <button className="inline-button danger-text" type="button" onClick={() => onCancelOrder(selectedOrder.id)} disabled={cancelling || ['shipped', 'delivered', 'cancelled'].includes(selectedOrder.status)}>
                  {cancelling ? 'Cancelando...' : 'Cancelar pedido'}
                </button>
              </div>
            </form>

            <SectionCard title="Itens do pedido" subtitle="Snapshot transacional do checkout.">
              <DataTable
                rows={selectedOrder.items || []}
                columns={[
                  { key: 'product_name_snapshot', label: 'Produto' },
                  { key: 'quantity', label: 'Qtd.' },
                  { key: 'unit_price', label: 'Unitário' },
                  { key: 'total_price', label: 'Total' },
                ]}
              />
            </SectionCard>

            <SectionCard title="Entrega" subtitle="Dados do cliente e endereço da operação.">
              <div className="detail-stack compact">
                <div><span className="eyebrow">Telefone</span><strong>{selectedOrder.customer_phone || '-'}</strong></div>
                <div><span className="eyebrow">Endereço</span><strong>{selectedOrder.address_line || '-'}</strong></div>
                <div><span className="eyebrow">Cidade/UF</span><strong>{selectedOrder.city || '-'} {selectedOrder.state || ''}</strong></div>
                <div><span className="eyebrow">CEP</span><strong>{selectedOrder.zip_code || '-'}</strong></div>
              </div>
            </SectionCard>

            <SectionCard title="Histórico operacional" subtitle="Auditoria de mudanças do pedido.">
              <div className="timeline-list">
                {(selectedOrder.history || []).length ? selectedOrder.history.map((entry) => (
                  <article key={entry.id} className="timeline-item">
                    <div className="timeline-item__minute">{entry.action_name}</div>
                    <div>
                      <strong>{entry.created_at}</strong>
                      <p>{entry.payload_json || '-'}</p>
                    </div>
                  </article>
                )) : <div className="empty-state">Sem histórico registrado ainda.</div>}
              </div>
            </SectionCard>
          </div>
        ) : (
          <div className="empty-state">Selecione um pedido para abrir o detalhe operacional.</div>
        )}
      </SectionCard>
    </div>
  );
}
